import * as React from 'react'
import type {Ref} from 'react'

import {
  useDocumentSearchHandler,
  useDocumentSearchOccurancesHandlers,
} from '../../../../../../hooks/documentSearch'
import type {BuildId} from '../../../../../../types'
import {getFullLogState, getLogFilter} from '../../../BuildLog.selectors'
import {SearchDirection} from '../../../BuildLog.types'
import {TargetContext} from '../../FullBuildLog.context'

import {clearBuildLogSearch, searchMessageInBuildLog} from './BuildLogSearch.actions'
import {getBuilLogSearchState} from './BuildLogSearch.selectors'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'

function useSearchInput(): {
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  focusInput: () => void
  onClear: () => void
  inputRef: Ref<HTMLInputElement>
} {
  const target = React.useContext(TargetContext)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [value, setValue] = React.useState<string>('')
  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.currentTarget.value
      setValue(newValue)
    },
    [setValue],
  )
  const focusInput = React.useCallback(() => {
    inputRef.current?.focus()
  }, [inputRef])
  const onClear = React.useCallback(() => {
    setValue('')
    clearBuildLogSearch(target)
  }, [setValue, target])
  return {
    value,
    onChange,
    focusInput,
    onClear,
    inputRef,
  }
}

function useCloseSearchHandlers(setSearchActive: (searchActive: boolean) => unknown): {
  onResetClick: () => unknown
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => unknown
} {
  const dispatch = useAppDispatch()
  const target = React.useContext(TargetContext)
  const closeSearch = React.useCallback(() => {
    setSearchActive(false)
    dispatch(clearBuildLogSearch(target))
  }, [setSearchActive, dispatch, target])
  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        closeSearch()
      }
    },
    [closeSearch],
  )
  return {
    onResetClick: closeSearch,
    onKeyDown,
  }
}

export function useBuildLogSearch(
  setSearchActive: (searchActive: boolean) => unknown,
  buildId: BuildId,
) {
  const target = React.useContext(TargetContext)
  const dispatch = useAppDispatch()
  const {logView} = useAppSelector(state => getFullLogState(state, target))
  const filter = useAppSelector(state => getLogFilter(state, target))
  const {query, loading, limitReached, notFound, nextStartId, startId, searchDirection} =
    useAppSelector(state => getBuilLogSearchState(state, target))
  const {value, focusInput, onChange, onClear, inputRef} = useSearchInput()
  const {onKeyDown: onKeyDownSearchHandler, onResetClick} = useCloseSearchHandlers(setSearchActive)
  useDocumentSearchHandler(true, focusInput)
  const searchStarted = query === value

  const nextDisabled = searchStarted && searchDirection === SearchDirection.NEXT && notFound
  const searchNext = React.useCallback(() => {
    if (value.length > 0 && !nextDisabled) {
      dispatch(
        searchMessageInBuildLog({
          searchDirection: SearchDirection.NEXT,
          query: value,
          startId:
            searchDirection === SearchDirection.PREVIOUS
              ? nextStartId != null
                ? nextStartId + 2
                : 0
              : searchStarted
                ? nextStartId ?? 0
                : 0,
          applyLimit: !limitReached,
          target,
          buildId,
          filter,
          logView,
        }),
      )
    }
  }, [
    buildId,
    dispatch,
    filter,
    nextStartId,
    limitReached,
    nextDisabled,
    searchDirection,
    searchStarted,
    target,
    value,
    logView,
  ])
  const previousDisabled = searchDirection === SearchDirection.PREVIOUS && notFound
  const searchPrevious = React.useCallback(() => {
    if (value.length > 0 && !previousDisabled) {
      dispatch(
        searchMessageInBuildLog({
          searchDirection: SearchDirection.PREVIOUS,
          query: value,
          startId:
            searchDirection === SearchDirection.NEXT && nextStartId != null
              ? nextStartId - 2
              : nextStartId ?? 0,
          applyLimit: !limitReached,
          target,
          buildId,
          filter,
          logView,
        }),
      )
    }
  }, [
    buildId,
    dispatch,
    filter,
    nextStartId,
    limitReached,
    previousDisabled,
    searchDirection,
    target,
    value,
    logView,
  ])

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      searchNext()
    },
    [searchNext],
  )

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault()
        event.stopPropagation()
        searchPrevious()
      } else {
        onKeyDownSearchHandler(event)
      }
    },
    [onKeyDownSearchHandler, searchPrevious],
  )

  useDocumentSearchOccurancesHandlers(searchStarted, searchPrevious, searchNext)
  return {
    inputRef,
    startId,
    value,
    searchStarted,
    searchDirection,
    notFound,
    limitReached,
    loading,
    nextDisabled,
    previousDisabled,
    searchPrevious,
    onSubmit,
    onResetClick,
    onClear,
    onKeyDown,
    onChange,
  }
}
